import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  HiOutlineBell,
  HiOutlineCalendar,
  HiOutlineChatAlt,
  HiOutlineMail,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomButton } from "../../../atoms/CustomButton";
import OptionToggleRow from "../../../atoms/OptionToggleRow";
import UnlockBadge from "../../../atoms/UnlockBadge";
import { PlansModal } from "../../../modals/PlansModal";
import TimeZoneSelector from "../entities/TimeZoneSelector";
import useAuth from "../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import {
  AGENDA_EMAIL_LANGUAGES,
  AgendaConfig,
  AgendaEmailLanguage,
  AgendaNotifications,
  useUpdateAgendaConfig,
} from "../../../../hooks/agenda";
import { browserTimeZone } from "./agendaDates";
import { useAgendaFailure } from "./agendaTypes";

const REMINDER_HOURS = [1, 2, 4, 12, 24, 48];
const DEFAULT_REMINDER_HOURS = 24;

export const DEFAULT_NOTIFICATIONS: AgendaNotifications = {
  confirmation: false,
  email_reminder_hours: null,
  member_reminder: false,
  sms_reminder: false,
};

export const notificationsToSave = (
  notifications: AgendaNotifications,
  canEmail: boolean,
  canSms: boolean
): Partial<AgendaNotifications> | undefined => {
  if (!canEmail) return undefined;
  const reminderOn = notifications.email_reminder_hours !== null;
  // A setting the plan no longer offers is left out, so the server keeps it for an upgrade.
  return {
    confirmation: notifications.confirmation,
    email_reminder_hours: notifications.email_reminder_hours,
    member_reminder: reminderOn && notifications.member_reminder,
    ...(canSms
      ? { sms_reminder: reminderOn && notifications.sms_reminder }
      : {}),
  };
};

export const emailLanguageOptions = (uiLanguage: string) => {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([uiLanguage], { type: "language" });
  } catch {
    names = null;
  }
  return AGENDA_EMAIL_LANGUAGES.map((code) => {
    const name = names?.of(code) || code;
    return {
      code,
      name: name.charAt(0).toLocaleUpperCase(uiLanguage) + name.slice(1),
    };
  }).sort((a, b) => a.name.localeCompare(b.name, uiLanguage));
};

interface AgendaConfigModalProps {
  show: boolean;
  onClose: () => void;
  config: AgendaConfig;
}

const AgendaConfigModal: React.FC<AgendaConfigModalProps> = ({
  show,
  onClose,
  config,
}) => {
  const { t, i18n } = useTranslation();
  const failure = useAgendaFailure();
  const { user, customer_identification: customerId } = useAuth();
  const entityId = user.entity_identification;
  const permissions = usePermissions(user);
  const canEmail = permissions.hasFeatureAccess("SEND_MESSAGES");
  const canSms = permissions.hasFeatureAccess("SEND_SMS");
  const save = useUpdateAgendaConfig(customerId, entityId);
  const [plansTarget, setPlansTarget] = useState<PlanTier | null>(null);

  const [enabled, setEnabled] = useState(config.enabled);
  const [timezone, setTimezone] = useState(
    config.timezone || browserTimeZone()
  );
  const [notifications, setNotifications] = useState<AgendaNotifications>({
    ...DEFAULT_NOTIFICATIONS,
    ...config.notifications,
  });
  const [memberLanguage, setMemberLanguage] =
    useState<AgendaEmailLanguage | null>(config.member_language);
  const languageOptions = useMemo(
    () => emailLanguageOptions(i18n.language),
    [i18n.language]
  );

  useEffect(() => {
    if (!show) return;
    setEnabled(config.enabled);
    setTimezone(config.timezone || browserTimeZone());
    setNotifications({ ...DEFAULT_NOTIFICATIONS, ...config.notifications });
    setMemberLanguage(config.member_language);
  }, [show, config]);

  const setNotification = <K extends keyof AgendaNotifications>(
    key: K,
    value: AgendaNotifications[K]
  ) => setNotifications((prev) => ({ ...prev, [key]: value }));

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save.mutate(
      {
        enabled,
        timezone,
        notifications: notificationsToSave(notifications, canEmail, canSms),
        ...(canEmail ? { member_language: memberLanguage } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("agenda.config.saved") as string);
          onClose();
        },
        onError: (err) => {
          toast.error(failure(err, "agenda.config.saveError"));
        },
      }
    );
  };

  const unlockBadge = (target: PlanTier) => (
    <UnlockBadge onClick={() => setPlansTarget(target)} />
  );
  const emailBadge = canEmail ? undefined : unlockBadge(PlanTier.PREMIUM);
  const smsBadge = canSms ? undefined : unlockBadge(PlanTier.ENTERPRISE);

  const reminderOn = canEmail && notifications.email_reminder_hours !== null;

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="xl"
        dismissible
        title={t("agenda.config.title")}
        subtitle={t("agenda.config.subtitle")}
        onSubmit={onSubmit}
        footer={
          <>
            <CustomButton
              type="button"
              variant="secondary"
              title={t("common.cancel") as string}
              onClick={onClose}
              disabled={save.isLoading}
            />
            <CustomButton
              type="submit"
              variant="primary"
              title={t("agenda.config.save") as string}
              isLoading={save.isLoading}
              disabled={save.isLoading}
            />
          </>
        }
      >
        <div className="space-y-6">
          <div className="divide-y divide-gray-100">
            <OptionToggleRow
              icon={HiOutlineCalendar}
              title={t("agenda.config.enabledTitle")}
              description={t("agenda.config.enabledDescription")}
              enabled={enabled}
              onChange={setEnabled}
              className="pt-0"
            />
            <div className="space-y-1 py-4">
              <label
                htmlFor="agenda-entity-timezone"
                className="block text-sm font-semibold text-gray-900"
              >
                {t("agenda.config.timezoneLabel")}
              </label>
              <p className="text-xs text-gray-500">
                {t("agenda.config.timezoneDescription")}
              </p>
              <TimeZoneSelector
                inputId="agenda-entity-timezone"
                currentTimeZone={timezone}
                onChange={setTimezone}
              />
            </div>
          </div>

          <div>
            <h4 className="border-b border-gray-200 pb-2 text-sm font-semibold text-gray-900">
              {t("agenda.config.notificationsTitle")}
            </h4>
            <div className="divide-y divide-gray-100">
              <OptionToggleRow
                icon={HiOutlineMail}
                title={t("agenda.config.confirmationTitle")}
                titleBadge={emailBadge}
                description={t("agenda.config.confirmationDescription")}
                enabled={canEmail && notifications.confirmation}
                onChange={(value) => setNotification("confirmation", value)}
                disabled={!canEmail}
              />
              <div>
                <OptionToggleRow
                  icon={HiOutlineBell}
                  title={t("agenda.config.reminderTitle")}
                  titleBadge={emailBadge}
                  description={t("agenda.config.reminderDescription")}
                  enabled={reminderOn}
                  onChange={(value) =>
                    setNotification(
                      "email_reminder_hours",
                      value ? DEFAULT_REMINDER_HOURS : null
                    )
                  }
                  disabled={!canEmail}
                />
                {reminderOn && (
                  <div className="-mt-2 flex flex-wrap items-center gap-2 pb-4 pl-8">
                    <label
                      htmlFor="agenda-reminder-hours"
                      className="text-sm text-gray-700"
                    >
                      {t("agenda.config.reminderDelayLabel")}
                    </label>
                    <select
                      id="agenda-reminder-hours"
                      value={notifications.email_reminder_hours ?? undefined}
                      onChange={(e) =>
                        setNotification(
                          "email_reminder_hours",
                          Number(e.target.value)
                        )
                      }
                      className="rounded-md border border-gray-300 bg-white p-2.5 pr-8 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
                    >
                      {REMINDER_HOURS.map((hours) => (
                        <option key={hours} value={hours}>
                          {t("agenda.config.hoursBefore", { count: hours })}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <OptionToggleRow
                icon={HiOutlineUserGroup}
                title={t("agenda.config.memberReminderTitle")}
                titleBadge={emailBadge}
                description={t("agenda.config.memberReminderDescription")}
                enabled={reminderOn && notifications.member_reminder}
                onChange={(value) => setNotification("member_reminder", value)}
                disabled={!reminderOn}
              />
              <OptionToggleRow
                icon={HiOutlineChatAlt}
                title={t("agenda.config.smsReminderTitle")}
                titleBadge={smsBadge}
                description={t("agenda.config.smsReminderDescription")}
                enabled={reminderOn && canSms && notifications.sms_reminder}
                onChange={(value) => setNotification("sms_reminder", value)}
                disabled={!reminderOn || !canSms}
              />
              <div className="space-y-1 py-4">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <label
                    htmlFor="agenda-member-language"
                    className="block text-sm font-semibold text-gray-900"
                  >
                    {t("agenda.config.memberLanguageLabel")}
                  </label>
                  {emailBadge}
                </div>
                <p
                  id="agenda-member-language-description"
                  className="text-xs text-gray-500"
                >
                  {t("agenda.config.memberLanguageDescription")}
                </p>
                <select
                  id="agenda-member-language"
                  aria-describedby="agenda-member-language-description"
                  value={memberLanguage ?? ""}
                  onChange={(e) =>
                    setMemberLanguage(
                      AGENDA_EMAIL_LANGUAGES.find(
                        (code) => code === e.target.value
                      ) ?? null
                    )
                  }
                  disabled={!canEmail}
                  className="block w-full rounded-md border border-gray-300 bg-white p-2.5 pr-8 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-72 md:py-2 md:text-sm"
                >
                  <option value="">
                    {t("agenda.config.memberLanguageDefault")}
                  </option>
                  {languageOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </ModalShell>
      {/* Outside the ModalShell: React events cross portals, so a submit from the plans would reach the settings form. */}
      {plansTarget && (
        <PlansModal
          showPlansModal
          setShowPlansModal={() => setPlansTarget(null)}
          targetPlan={plansTarget}
        />
      )}
    </>
  );
};

export default AgendaConfigModal;
